import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { 
  FaHome,
  FaPhone,
  FaEnvelope,
  FaArrowRight,
  FaSearch,
  FaFileSignature,
  FaHandshake,
  FaUserCheck
} from 'react-icons/fa';
import { title as siteTitle } from '../constants/constant';
import Layout from '../components/layout/Layout';
import BreadcrumbImg from '../assets/images/breadcrumb.png';
import { headerData } from '../data/headerData';

// Constants Data
const breadcrumbData = {
  home: { text: "Home", path: "/" },
  current: "Admissions",
  title: "Admissions",
  image: BreadcrumbImg
};

const processSteps = [
  {
    id: 1,
    step: "01",
    title: "Inquiry",
    description: "Visit school or website for information",
    icon: FaSearch,
    gradient: "from-blue-500 to-blue-600",
    bgColor: "bg-blue-100",
    iconColor: "text-blue-600"
  },
  {
    id: 2,
    step: "02",
    title: "Application",
    description: "Fill admission form with documents",
    icon: FaFileSignature,
    gradient: "from-emerald-500 to-emerald-600",
    bgColor: "bg-emerald-100",
    iconColor: "text-emerald-600"
  },
  {
    id: 3,
    step: "03",
    title: "Interaction",
    description: "Student & parent interaction session",
    icon: FaHandshake,
    gradient: "from-purple-500 to-purple-600",
    bgColor: "bg-purple-100",
    iconColor: "text-purple-600"
  },
  {
    id: 4,
    step: "04",
    title: "Confirmation",
    description: "Fee payment & admission confirmation",
    icon: FaUserCheck,
    gradient: "from-amber-500 to-amber-600",
    bgColor: "bg-amber-100",
    iconColor: "text-amber-600"
  }
];

const contactData = {
  title: "Need Help with Admission?",
  subtitle: "Our admission team is here to assist you",
  phone: headerData.contactInfo.phone.number,
  email: headerData.contactInfo.email.address
};

const faqData = [
  {
    id: 1,
    question: "What is the admission age criteria?",
    answer: "Pre-Nursery: 2.5-3.5 years, Nursery: 3.5-4.5 years, and so on as per CBSE guidelines."
  },
  {
    id: 2,
    question: "Is there any entrance test?",
    answer: "For Pre-Nursery to UKG: Interaction only. For Class 1 onwards: Basic aptitude assessment."
  },
  {
    id: 3,
    question: "What are the school timings?",
    answer: "8:30 AM to 2:30 PM (Monday to Saturday). Saturday is a working day."
  },
  {
    id: 4,
    question: "Is transport facility available?",
    answer: "Yes, GPS-enabled buses with trained attendants cover all major areas."
  }
];

const footerNote = "* All admissions are subject to availability of seats and fulfillment of eligibility criteria.";

export default function Admissions() {
  const [openFaq, setOpenFaq] = useState(1);

  const toggleFaq = (id) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <Layout>
      <Helmet>
        <title>Admissions | {siteTitle}</title>
        <meta name="description" content="Apply for admissions at Gyan Rashmi. Learn about admission process, eligibility criteria, and important dates." />
      </Helmet>

      {/* Breadcrumb Section */}
      <div className="relative bg-gray-900">
        <div className="h-40 md:h-48">
          <img 
            alt="Breadcrumb" 
            className="object-cover w-full h-full opacity-60" 
            src={breadcrumbData.image} 
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="flex items-center mb-2 text-sm text-white">
            <a 
              href={breadcrumbData.home.path} 
              className="flex items-center transition-colors hover:text-gray-200"
            >
              <FaHome className="w-3 h-3 mr-1.5" />
              {breadcrumbData.home.text}
            </a>
            <span className="mx-2">/</span>
            <span className="font-semibold">{breadcrumbData.current}</span>
          </div>
          <h1 className="text-2xl font-bold text-white md:text-3xl">{breadcrumbData.title}</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container px-3 py-5 mx-auto max-w-7xl md:px-4 md:py-6">
        {/* Welcome Section */}
        <div className="max-w-2xl mx-auto mb-6 text-center">
          <h2 className="mb-2 text-xl font-bold text-gray-900 md:text-2xl">Welcome to Our School</h2>
          <p className="text-sm text-gray-700 md:text-base">
            We are now accepting applications for the academic year 2026-27. 
            Our admission process is designed to be transparent and student-friendly.
          </p>
        </div>

        {/* Process Steps - Compact Grid */}
        <div className="mb-6">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {processSteps.map((step) => (
              <div 
                key={step.id}
                className="relative p-4 text-center transition-all duration-300 bg-white border border-gray-200 rounded-xl hover:shadow-md hover:-translate-y-1 group"
              >
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${step.bgColor} mb-2 group-hover:scale-110 transition-transform duration-300`}>
                  <step.icon className={`w-5 h-5 ${step.iconColor}`} />
                </div>

                {/* Step Number */}
                <div className={`text-xs font-semibold bg-gradient-to-r ${step.gradient} bg-clip-text text-transparent mb-1`}>
                  Step {step.step}
                </div>

                {/* Title */}
                <h3 className="mb-1 text-sm font-bold text-gray-900">{step.title}</h3>

                {/* Description */}
                <p className="text-xs leading-tight text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Banner - Compact */}
        <div className="p-4 mb-6 text-white bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-3 sm:mb-0">
              <h3 className="text-base font-bold">{contactData.title}</h3>
              <p className="text-xs text-blue-100">{contactData.subtitle}</p>
            </div>
            <div className="flex gap-2">
              <a 
                href={`tel:${contactData.phone}`} 
                className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-blue-700 bg-white rounded-lg hover:bg-blue-50 transition-all"
              >
                <FaPhone className="w-3 h-3" />
                Call
              </a>
              <a 
                href={`mailto:${contactData.email}`} 
                className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-transparent border border-white rounded-lg hover:bg-white/10 transition-all"
              >
                <FaEnvelope className="w-3 h-3" />
                Email
              </a>
            </div>
          </div>
        </div>

        {/* FAQ Section - Compact */}
        <div className="mb-4">
          <h2 className="mb-3 text-lg font-bold text-gray-900">Frequently Asked Questions</h2>

          <div className="space-y-2">
            {faqData.map((faq) => (
              <div key={faq.id} className="overflow-hidden border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleFaq(faq.id)}
                  className="flex items-center justify-between w-full p-3 text-left transition-colors bg-gray-50 hover:bg-gray-100"
                >
                  <span className="text-xs font-medium text-gray-900">{faq.question}</span>
                  <FaArrowRight 
                    className={`w-3 h-3 text-gray-500 transition-transform duration-300 ${
                      openFaq === faq.id ? 'rotate-90' : ''
                    }`} 
                  />
                </button>
                {openFaq === faq.id && (
                  <div className="p-3 bg-white border-t border-gray-200">
                    <p className="text-xs text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center">
          <p className="text-xs text-gray-600">{footerNote}</p>
        </div>
      </div>
    </Layout>
  );
}